import classNames from 'classnames';
import { useFormik } from 'formik';
import Cookies from 'js-cookie';
import { FC, useCallback, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CantoiaLogo from '../../../assets/img/logos/cantoia.png';
import MESSAGES from '../../../common/data/constants/messages';
import Alert from '../../../components/bootstrap/Alert';
import Button from '../../../components/bootstrap/Button';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import Spinner from '../../../components/bootstrap/Spinner';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import AuthContext from '../../../contexts/authContext';
import useDarkMode from '../../../hooks/useDarkMode';
import Page from '../../../layout/Page/Page';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import authService from '../../../services/auth-service';

export const TokenPage = () => {
    const { darkModeStatus } = useDarkMode();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [resendDisabled, setResendDisabled] = useState<boolean>(true);
    const [countdown, setCountdown] = useState<number>(60);
    const [resendButtonText, setResendButtonText] = useState<string>('Reenviar em 01:00');
    const [timerKey, setTimerKey] = useState<number>(0);

    const { setUser } = useContext(AuthContext);

    const navigate = useNavigate();
    const handleOnBackClick = useCallback(() => navigate('/auth-pages/login'), [navigate]);

    // Função para iniciar ou reiniciar o temporizador de contagem regressiva
    const startCountdownTimer = useCallback(() => {
        setResendDisabled(true);
        setCountdown(60);
        setResendButtonText('Reenviar em 01:00');
        setTimerKey(prevKey => prevKey + 1); // Incrementar a chave do temporizador para acionar o useEffect
    }, []);

    // Inicializar e gerenciar o temporizador de contagem regressiva
    useEffect(() => {
        // Redefinir valores de contagem regressiva quando timerKey mudar
        setResendDisabled(true);
        setCountdown(60);
        setResendButtonText('Reenviar em 01:00');

        // Limpar quaisquer temporizadores existentes quando timerKey mudar
        let timer: NodeJS.Timeout;

        // Configurar intervalo para atualizar a contagem regressiva
        timer = setInterval(() => {
            setCountdown((prevCountdown) => {
                if (prevCountdown <= 1) {
                    // Temporizador chegou a zero, habilitar botão e atualizar texto
                    clearInterval(timer);
                    setResendDisabled(false);
                    setResendButtonText('Reenviar código');
                    return 0;
                } else {
                    // Atualizar texto do temporizador
                    const newCount = prevCountdown - 1;
                    const minutes = Math.floor(newCount / 60);
                    const seconds = newCount % 60;
                    setResendButtonText(`Reenviar em ${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
                    return newCount;
                }
            });
        }, 1000);

        // Limpar intervalo na desmontagem do componente ou quando timerKey mudar
        return () => clearInterval(timer);
    }, [timerKey]); // Adicionar timerKey ao array de dependências para reiniciar o temporizador quando ele mudar

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            token: '',
        },
        validate: (values) => {
            const errors: { token?: string } = {};

            if (!values.token) {
                errors.token = MESSAGES.VALIDATION.REQUIRED_FIELD;
            }

            return errors;
        },
        validateOnChange: false,
        onSubmit: async (values) => {
            handleSubmit(values);
        },
    });

    const reSendSecurityToken = async () => {
        setMessage('');

        const email = Cookies.get('userEmail') || '';
        if (!email) {
            setMessage('Email não encontrado. Por favor, faça login novamente.');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const response = await authService.resendConfirmationCode(email);

        setIsLoading(false);

        if (response.success) {
            setIsSuccess(true);
            setMessage('Código re-enviado para seu email.');
            // Reiniciar o temporizador de contagem regressiva após o envio bem-sucedido do código
            startCountdownTimer();
        } else {
            setIsSuccess(false);
            setMessage(response.message || 'Erro ao enviar código de segurança.');
        }
    };

    const handleSubmit = async (values: any) => {
        try {

            setMessage('');

            const email = Cookies.get('userEmail') || '';
            if (!email) {
                setMessage(MESSAGES.LOGIN.EMAIL_NOT_FOUN);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);

            const response = await authService.sendToken({
                email,
                otp: values.token,
                session: Cookies.get('session') || ''
            });

            if (response.success) {

                setIsSuccess(true);
                Cookies.set('token', response.entity?.idToken!);

                setMessage(MESSAGES.LOGIN.VERIFICATION_CODE_SUCCESS);

                if (setUser) {
                    setUser(email);
                }

                setTimeout(() => {
                    navigate('/');
                }, 2000);
            } else {
                setIsSuccess(false);
                setMessage(response.message || MESSAGES.LOGIN.VERIFICATION_CODE_ERROR);
            }
        } catch (error) {
            setIsSuccess(false);
            setMessage(MESSAGES.LOGIN.VERIFICATION_CODE_ERROR);
        } finally {
            setIsLoading(false);
        }
    }

    const TwoFactorHeader: FC = () => {
        return (
            <>
                <div className="text-center h4 fw-bold mt-5">Autenticação em duas etapas</div>

                <div className="text-center h5 text-muted mb-5">Insira o código enviado para seu e-mail</div>
            </>
        );
    };

    return (
        <PageWrapper isProtected={false} title="Código de Segurança" className={classNames({ 'bg-dark': !darkModeStatus, 'bg-light': darkModeStatus })}>
            <Page>
                <div className="row h-100 align-items-center justify-content-center">
                    <div className="col-xl-4 col-lg-6 col-md-8 shadow-3d-container">
                        <Card className="shadow-3d-dark" data-tour="token-page">
                            <CardBody>
                                <div className="text-center my-1">
                                    <img src={CantoiaLogo} alt='Cantoia' width={300} />
                                </div>

                                <TwoFactorHeader />

                                {message && (
                                    <Alert
                                        color={isSuccess ? 'success' : 'danger'}
                                        isLight
                                        className="mb-4"
                                        icon={isSuccess ? 'Check' : 'Warning'}
                                    >
                                        {message}
                                    </Alert>
                                )}

                                <form className="row g-3">
                                    <>
                                        <div className="col-12">
                                            <FormGroup id="token" isFloating label="Código de Autenticação">
                                                <Input
                                                    type="text"
                                                    value={formik.values.token}
                                                    isTouched={formik.touched.token}
                                                    invalidFeedback={formik.errors.token}
                                                    isValid={formik.isValid}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    onFocus={() => {
                                                        formik.setErrors({});
                                                    }}
                                                    disabled={isLoading}
                                                />
                                            </FormGroup>
                                        </div>

                                        <div className='col-xl-12 col-lg-12 col-md-12 col-sm-12'>
                                            <Button
                                                color="info"
                                                className="py-3 flex-grow-1 w-100"
                                                onClick={formik.submitForm}
                                                isDisable={isLoading}
                                            >
                                                {isLoading && <Spinner isSmall inButton isGrow />}
                                                Validar Código
                                            </Button>
                                        </div>

                                        <div className='col-xl-6 col-lg-6 col-md-6 col-sm-12'>
                                            <Button
                                                color="info"
                                                isOutline={true}
                                                className="py-3 flex-grow-1 w-100"
                                                onClick={reSendSecurityToken}
                                                isDisable={isLoading || resendDisabled}
                                            >
                                                {resendButtonText}
                                            </Button>
                                        </div>

                                        <div className='col-xl-6 col-lg-6 col-md-6 col-sm-12'>
                                            <Button
                                                color="info"
                                                isOutline={true}
                                                className="py-3 flex-grow-1 w-100"
                                                isDisable={isLoading}
                                                onClick={handleOnBackClick}
                                            >
                                                Voltar
                                            </Button>
                                        </div>
                                    </>
                                </form>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </Page>
        </PageWrapper>
    );
};
